/* =========================================================
   CREA TU PACK · TwoFive
   Configurador de packs (ES module, no dependencies).
   ========================================================= */

const STORAGE_KEY = 'twofive_pack_state';
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

class CreaTuPack {
  constructor(root) {
    this.root = root;
    // Tamaños de pack habilitados desde el theme editor (data-enabled-sizes
    // "2,3,4"). Si por algún motivo el atributo no llega, caemos al set
    // completo para no romper la página.
    var enabledCsv = (root.dataset.enabledSizes || '2,3,4').trim();
    this.maxSizes = enabledCsv
      .split(',')
      .map(function (n) { return parseInt(n, 10); })
      .filter(function (n) { return !isNaN(n) && [2, 3, 4].indexOf(n) !== -1; });
    if (this.maxSizes.length === 0) this.maxSizes = [2, 3, 4];
    this.discountByCount = {
      2: parseFloat(root.dataset.discount2 || '5'),
      3: parseFloat(root.dataset.discount3 || '10'),
      4: parseFloat(root.dataset.discount4 || '15'),
    };
    this.moneyFormat = root.dataset.moneyFormat || '€{{amount}}';

    this.packSize = parseInt(root.dataset.packSize || '3', 10);
    this.items = []; // { variantId, productId, productHandle, productTitle, productUrl, sizeLabel, price, imgSrc }
    this.filters = { cut: new Set(), glove: new Set(), palm: new Set(), range: new Set() };

    // Personalization fee (single charge per pack regardless of how many gloves are personalized)
    this.persoVariantId = root.dataset.personalizationVariant || null;
    this.persoPrice = parseInt(root.dataset.personalizationPrice || '0', 10);
    this.root = root;

    this.cache = {
      shell: root.querySelector('.ctp__shell'),
      catalog: root.querySelector('.ctp__catalog'),
      grid: root.querySelector('[data-ctp-grid]'),
      noResults: root.querySelector('[data-ctp-no-results]'),
      chips: root.querySelector('[data-ctp-chips]'),
      cards: Array.from(root.querySelectorAll('[data-ctp-card]')),
      filters: Array.from(root.querySelectorAll('[data-ctp-filter]')),
      slots: root.querySelector('[data-ctp-slots]'),
      sizeBtns: Array.from(root.querySelectorAll('.ctp__size-btn')),
      savings: root.querySelector('[data-ctp-savings]'),
      savingsText: root.querySelector('[data-ctp-savings-text]'),
      totalLabel: root.querySelector('[data-ctp-total-label]'),
      totalAmount: root.querySelector('[data-ctp-total-amount]'),
      progress: root.querySelector('[data-ctp-progress]'),
      checkout: root.querySelector('[data-ctp-checkout]'),
      checkoutText: root.querySelector('[data-ctp-checkout-text]'),
      spinner: root.querySelector('.ctp__spinner'),
      announce: root.querySelector('[data-ctp-announce]'),
      toast: root.querySelector('[data-ctp-toast]'),
      personalizeBtn: root.querySelector('[data-ctp-personalize]'),
      personalizeModal: root.querySelector('[data-ctp-personalize-modal]'),
      personalizeList: root.querySelector('[data-ctp-personalize-list]'),
      personalizeEmpty: root.querySelector('[data-ctp-personalize-empty]'),
      personalizeCloseEls: Array.from(root.querySelectorAll('[data-ctp-personalize-close]')),
      personalizeSaveBtn: root.querySelector('[data-ctp-personalize-save]'),
      infoBtn: root.querySelector('[data-ctp-info-open]'),
      infoModal: root.querySelector('[data-ctp-info-modal]'),
      infoCloseEls: Array.from(root.querySelectorAll('[data-ctp-info-close]')),
      cartBar: root.querySelector('[data-ctp-cart-bar]'),
      cartProgress: root.querySelector('[data-ctp-cart-progress]'),
      cartExpanded: root.querySelector('[data-ctp-cart-expanded]'),
      cartList: root.querySelector('[data-ctp-cart-list]'),
      cartEmpty: root.querySelector('[data-ctp-cart-empty]'),
      cartCount: root.querySelector('[data-ctp-cart-count]'),
      cartTotal: root.querySelector('[data-ctp-cart-total]'),
      cartCloseEls: Array.from(root.querySelectorAll('[data-ctp-cart-close]')),
      cartToggle: root.querySelector('[data-ctp-cart-toggle]'),
      cartToggleLabel: root.querySelector('[data-ctp-cart-toggle-label]'),
      cartFinalize: root.querySelector('[data-ctp-cart-finalize]'),
      cartFinalizeLabel: root.querySelector('[data-ctp-cart-finalize-label]'),
      cartSpinner: root.querySelector('[data-ctp-cart-spinner]'),
      panel: root.querySelector('.ctp__panel'),
    };

    this._init();
  }

  _init() {
    this._restoreState();
    this._readUrlFilters();
    this._wireFilters();
    this._wireCards();
    this._wireSizeSelector();
    this._wireCheckout();
    this._wirePersonalize();
    this._wireCartBar();
    this._renderSlots();
    this._renderSizeSelector();
    this._renderChips();
    this._applyFilters();
    this._renderTotals();
    this._renderCartBar();
    this._toggleCartBar();
    window.addEventListener('resize', () => this._toggleCartBar());
  }

  /* ------------------------------------------------------- Filters */
  _wireFilters() {
    this.cache.filters.forEach((filterEl) => {
      const id = filterEl.dataset.filterId;
      const trigger = filterEl.querySelector('[data-ctp-filter-trigger]');
      const panel = filterEl.querySelector('[data-ctp-filter-panel]');
      const closeBtn = filterEl.querySelector('[data-ctp-filter-close]');
      const clearBtn = filterEl.querySelector('[data-ctp-filter-clear]');
      const inputs = Array.from(filterEl.querySelectorAll('[data-ctp-filter-input]'));

      trigger.addEventListener('click', () => {
        const isOpen = panel.hasAttribute('hidden') === false;
        if (isOpen) {
          this._closeAllFilters();
          return;
        }
        this._closeAllFilters();
        panel.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        const firstInput = panel.querySelector('input');
        if (firstInput) firstInput.focus();
      });

      closeBtn.addEventListener('click', () => {
        panel.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      });

      clearBtn.addEventListener('click', () => {
        inputs.forEach((i) => { i.checked = false; });
        this.filters[id].clear();
        this._afterFilterChange();
      });

      inputs.forEach((input) => {
        if (this.filters[id].has(input.value)) {
          input.checked = true;
        }
        input.addEventListener('change', () => {
          if (input.checked) this.filters[id].add(input.value);
          else this.filters[id].delete(input.value);
          this._afterFilterChange();
        });
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-ctp-filter]')) {
        this._closeAllFilters();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._closeAllFilters();
    });

    // Clear-all button in no-results state
    const clearAll = this.root.querySelector('[data-ctp-clear-all]');
    if (clearAll) {
      clearAll.addEventListener('click', () => this._clearAllFilters());
    }
  }

  _closeAllFilters() {
    this.cache.filters.forEach((filterEl) => {
      const panel = filterEl.querySelector('[data-ctp-filter-panel]');
      const trigger = filterEl.querySelector('[data-ctp-filter-trigger]');
      panel.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  _afterFilterChange() {
    this._renderFilterCounts();
    this._renderChips();
    this._applyFilters();
    this._writeUrlFilters();
  }

  _clearAllFilters() {
    Object.keys(this.filters).forEach((k) => this.filters[k].clear());
    this.cache.filters.forEach((filterEl) => {
      filterEl.querySelectorAll('[data-ctp-filter-input]').forEach((i) => { i.checked = false; });
    });
    this._afterFilterChange();
  }

  _renderFilterCounts() {
    this.cache.filters.forEach((filterEl) => {
      const id = filterEl.dataset.filterId;
      const count = this.filters[id].size;
      const countEl = filterEl.querySelector('[data-ctp-filter-count]');
      if (count > 0) {
        countEl.textContent = String(count);
        countEl.removeAttribute('hidden');
        filterEl.dataset.active = 'true';
      } else {
        countEl.setAttribute('hidden', '');
        filterEl.dataset.active = 'false';
      }
    });
  }

  _renderChips() {
    const chips = this.cache.chips;
    chips.innerHTML = '';
    let hasAny = false;
    Object.entries(this.filters).forEach(([id, set]) => {
      set.forEach((value) => {
        hasAny = true;
        const chip = document.createElement('span');
        chip.className = 'ctp-chip';
        chip.innerHTML = `
          <span>${this._escapeHtml(value)}</span>
          <button type="button" class="ctp-chip__remove" aria-label="Quitar filtro: ${this._escapeAttr(value)}">✕</button>
        `;
        chip.querySelector('button').addEventListener('click', () => {
          this.filters[id].delete(value);
          // Uncheck the corresponding input
          const filterEl = this.cache.filters.find((f) => f.dataset.filterId === id);
          if (filterEl) {
            const input = filterEl.querySelector(`[data-ctp-filter-input][value="${CSS.escape(value)}"]`);
            if (input) input.checked = false;
          }
          this._afterFilterChange();
        });
        chips.appendChild(chip);
      });
    });
    if (hasAny) {
      const clearAll = document.createElement('span');
      clearAll.className = 'ctp-chip ctp-chip--clear';
      clearAll.innerHTML = `<span>Limpiar todo</span>
        <button type="button" class="ctp-chip__remove" aria-label="Quitar todos los filtros">✕</button>`;
      clearAll.querySelector('button').addEventListener('click', () => this._clearAllFilters());
      chips.appendChild(clearAll);
    }
  }

  _applyFilters() {
    const filterMap = {
      cut: 'cut', glove: 'glove', palm: 'palm', range: 'range'
    };
    let visibleCount = 0;
    this.cache.cards.forEach((card) => {
      let visible = true;
      for (const [filterId, attr] of Object.entries(filterMap)) {
        const set = this.filters[filterId];
        if (set.size === 0) continue;
        const cardVal = card.dataset[attr] || '';
        if (!set.has(cardVal)) {
          visible = false;
          break;
        }
      }
      if (visible) {
        card.hidden = false;
        visibleCount++;
      } else {
        card.hidden = true;
      }
    });
    this.cache.catalog.dataset.noResults = visibleCount === 0 && this.cache.cards.length > 0 ? 'true' : 'false';
  }

  _readUrlFilters() {
    const url = new URL(window.location.href);
    ['cut', 'glove', 'palm', 'range'].forEach((id) => {
      const v = url.searchParams.get(id);
      if (!v) return;
      v.split(',').forEach((val) => {
        const decoded = decodeURIComponent(val);
        if (decoded) this.filters[id].add(decoded);
      });
    });
  }

  _writeUrlFilters() {
    const url = new URL(window.location.href);
    Object.entries(this.filters).forEach(([id, set]) => {
      if (set.size > 0) {
        url.searchParams.set(id, Array.from(set).map(encodeURIComponent).join(','));
      } else {
        url.searchParams.delete(id);
      }
    });
    window.history.replaceState({}, '', url);
  }

  /* ------------------------------------------------------- Cards */
  _wireCards() {
    this.cache.cards.forEach((card) => {
      const addBtn = card.querySelector('[data-ctp-add]');
      const select = card.querySelector('[data-ctp-size-select]');
      const tip = card.querySelector('[data-ctp-size-tip]');

      select.addEventListener('change', () => {
        if (tip) tip.hidden = true;
        this._refreshCardState(card);
      });

      addBtn.addEventListener('click', () => {
        if (this._isCardInPack(card)) {
          this._removeFromPack(this._getCardItemKey(card));
          return;
        }
        if (!select.value) {
          if (tip) {
            tip.hidden = false;
            setTimeout(() => { tip.hidden = true; }, 2400);
          }
          card.dataset.shake = 'true';
          setTimeout(() => { delete card.dataset.shake; }, 360);
          return;
        }
        if (this.items.length >= this.packSize) {
          this._showFullPackToast();
          return;
        }
        this._addToPack(card);
      });

      this._refreshCardState(card);
    });
  }

  _refreshCardState(card) {
    const addBtn = card.querySelector('[data-ctp-add]');
    const labelEl = card.querySelector('[data-ctp-add-label]');
    const select = card.querySelector('[data-ctp-size-select]');

    const inPack = this._isCardInPack(card);
    if (inPack) {
      addBtn.dataset.state = 'added';
      labelEl.textContent = 'AÑADIDO';
      return;
    }
    if (this.items.length >= this.packSize) {
      addBtn.dataset.state = 'full';
      labelEl.textContent = 'PACK COMPLETO';
      return;
    }
    addBtn.dataset.state = '';
    labelEl.textContent = addBtn.dataset.defaultLabel || 'AÑADIR';

    const allOutOfStock = Array.from(select.options)
      .filter((o) => o.value)
      .every((o) => o.disabled);
    if (allOutOfStock) {
      select.disabled = true;
      addBtn.dataset.state = 'full';
      labelEl.textContent = 'AGOTADO';
    }
  }

  _refreshAllCardStates() {
    this.cache.cards.forEach((card) => this._refreshCardState(card));
  }

  _isCardInPack(card) {
    const key = this._getCardItemKey(card);
    return this.items.some((i) => i.key === key);
  }

  _getCardItemKey(card) {
    const select = card.querySelector('[data-ctp-size-select]');
    const variantId = select.value;
    return `${card.dataset.productId}:${variantId}`;
  }

  _addToPack(card) {
    const select = card.querySelector('[data-ctp-size-select]');
    const variantOption = select.options[select.selectedIndex];
    const sizeLabel = variantOption.textContent.replace(/\s*\(Sin stock\)\s*$/, '').trim();
    const price = parseInt(variantOption.dataset.price, 10);
    const img = card.querySelector('[data-ctp-card-img]');

    const item = {
      key: `${card.dataset.productId}:${select.value}`,
      variantId: select.value,
      productId: card.dataset.productId,
      productHandle: card.dataset.productHandle,
      productTitle: card.dataset.productTitle,
      productUrl: card.dataset.productUrl,
      sizeLabel,
      price,
      imgSrc: img ? img.currentSrc || img.src : '',
    };
    this.items.push(item);
    this._announce(`${card.dataset.productTitle} añadido al pack. Pack: ${this.items.length} de ${this.packSize} guantes.`);
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  _removeFromPack(key) {
    const item = this.items.find((i) => i.key === key);
    this.items = this.items.filter((i) => i.key !== key);
    if (item) {
      this._announce(`${item.productTitle} retirado del pack. Pack: ${this.items.length} de ${this.packSize} guantes.`);
    }
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  /* ------------------------------------------------------- Pack size */
  _wireSizeSelector() {
    this.cache.sizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const newSize = parseInt(btn.dataset.size, 10);
        this._changePackSize(newSize);
      });
    });
  }

  _renderSizeSelector() {
    this.cache.sizeBtns.forEach((btn) => {
      const size = parseInt(btn.dataset.size, 10);
      btn.setAttribute('aria-checked', size === this.packSize ? 'true' : 'false');
    });
  }

  _changePackSize(newSize) {
    if (!this.maxSizes.includes(newSize)) return;
    if (newSize < this.items.length) {
      const drop = this.items.length - newSize;
      const ok = window.confirm(
        `Vas a reducir tu pack a ${newSize} guantes. Se quitarán los últimos ${drop} guantes seleccionados. ¿Continuar?`
      );
      if (!ok) return;
      this.items = this.items.slice(0, newSize);
    }
    this.packSize = newSize;
    this._renderSizeSelector();
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  /* ------------------------------------------------------- Slots */
  _renderSlots() {
    const container = this.cache.slots;
    container.style.setProperty('--ctp-slot-count', this.packSize);
    container.dataset.count = String(this.packSize);
    container.innerHTML = '';
    for (let i = 0; i < this.packSize; i++) {
      const item = this.items[i];
      const slot = document.createElement('div');
      slot.className = 'ctp-slot';
      slot.setAttribute('role', 'listitem');
      slot.dataset.index = String(i);
      if (item) {
        slot.classList.add('ctp-slot--filled');
        slot.innerHTML = `
          <img class="ctp-slot__img" src="${this._escapeAttr(item.imgSrc)}" alt="${this._escapeAttr(item.productTitle)}" loading="lazy">
          <button type="button" class="ctp-slot__remove" aria-label="Quitar ${this._escapeAttr(item.productTitle)} del pack, posición ${i + 1}">✕</button>
        `;
        slot.title = `${item.productTitle} · Talla ${item.sizeLabel}`;
        slot.querySelector('button').addEventListener('click', () => this._removeFromPack(item.key));
      } else {
        slot.classList.add('ctp-slot--empty');
        slot.innerHTML = `
          <button type="button" class="ctp-slot__cta" aria-label="Slot vacío ${i + 1}. Pulsa para elegir un guante."></button>
        `;
        slot.querySelector('button').addEventListener('click', () => {
          this.cache.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      container.appendChild(slot);
    }
  }

  _hasAnyPersonalization() {
    return this.items.some((item) => {
      const p = item.personalization;
      if (!p) return false;
      const r = (p.right || '').trim();
      const l = (p.left || '').trim();
      return Boolean(r || l);
    });
  }

  _personalizationFeeCents() {
    return (this.persoVariantId && this._hasAnyPersonalization()) ? this.persoPrice : 0;
  }

  /* ------------------------------------------------------- Totals */
  // Lee SIEMPRE el % desde root.dataset (no del cache del constructor) para
  // que el theme editor refleje cambios de "Reglas de descuento" sin tener
  // que reiniciar manualmente: cuando Shopify re-renderiza la sección, los
  // data-attributes traen los valores nuevos y el siguiente _renderTotals
  // los usa al instante.
  _discountPctForPack(size) {
    // dataset accessor quirk: en HTML data-attrs, un guion seguido de
    // DÍGITO (no letra) no se camel-casea — `data-discount-2` mapea a
    // dataset['discount-2'], NO a dataset.discount2. Por eso ahora usamos
    // `data-discount2` sin guion (mapea a dataset.discount2). Mantenemos
    // la lectura defensiva por ambos nombres para no romper si en el
    // futuro alguien renombra el atributo.
    const ds = this.root.dataset || {};
    const fallback = { 2: 5, 3: 10, 4: 15 };
    const raw = ds['discount' + size] != null
                  ? ds['discount' + size]
                  : ds['discount-' + size];
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) return parsed;
    return fallback[size] || 0;
  }

  _renderTotals() {
    const subtotal = this.items.reduce((sum, i) => sum + i.price, 0);
    const isComplete = this.items.length === this.packSize;
    const discountPct = this._discountPctForPack(this.packSize);
    // Keep the cached map in sync so otras llamadas (p.ej. _renderCartBar)
    // que aún lean this.discountByCount tampoco se queden obsoletas.
    this.discountByCount[this.packSize] = discountPct;
    const discountAmount = isComplete ? Math.round(subtotal * discountPct) / 100 : 0;
    const persoFee = this._personalizationFeeCents();
    const total = subtotal - discountAmount + persoFee;

    this.cache.totalLabel.textContent = `${this.packSize} PACK:`;
    if (this.items.length === 0) {
      this.cache.totalAmount.textContent = '—€';
    } else {
      const amountStr = this._formatMoney(Math.round(total));
      this.cache.totalAmount.textContent = amountStr;
      this.cache.totalAmount.dataset.pulse = 'true';
      setTimeout(() => { delete this.cache.totalAmount.dataset.pulse; }, 320);
    }

    // Savings badge is ALWAYS visible — always show some text (default = "Ahorra hasta X%")
    const savingsEl = this.cache.savings;
    savingsEl.hidden = false;
    savingsEl.classList.remove('ctp__savings--invisible');
    if (isComplete && discountAmount > 0) {
      this.cache.savingsText.textContent = `Ahorra ${this._formatMoney(Math.round(discountAmount))}`;
    } else if (this.items.length > 0 && this.items.length < this.packSize && discountPct > 0) {
      const avgPrice = subtotal / this.items.length;
      const projectedSubtotal = avgPrice * this.packSize;
      const projectedSavings = Math.round((projectedSubtotal * discountPct) / 100);
      this.cache.savingsText.textContent = `Ahorra hasta ${this._formatMoney(projectedSavings)}`;
    } else {
      // Empty pack — show motivational text with the % off for current packSize
      this.cache.savingsText.textContent = `Ahorra hasta ${discountPct}%`;
    }

    if (this.items.length === 0) {
      this.cache.progress.textContent = '';
    } else if (this.items.length < this.packSize) {
      this.cache.progress.textContent = `Tu pack tiene ${this.items.length} de ${this.packSize} guantes`;
    } else {
      this.cache.progress.textContent = '¡Pack completo!';
    }

    const ready = this.items.length >= this.packSize;
    this.cache.checkout.disabled = !ready;
    if (ready) {
      this.cache.checkoutText.textContent = 'FINALIZAR COMPRA';
    } else {
      const missing = this.packSize - this.items.length;
      this.cache.checkoutText.textContent = `FALTA${missing > 1 ? 'N' : ''} ${missing} GUANTE${missing > 1 ? 'S' : ''}`;
    }

    // Mirror state into the mobile cart bar
    this._renderCartBar();
  }

  /* ------------------------------------------------------- Checkout */
  _wireCheckout() {
    this.cache.checkout.addEventListener('click', async () => {
      if (this.cache.checkout.disabled) return;
      try {
        this._setCheckoutLoading(true);
        await this._submitToCart();
        this._clearStateAfterCheckout();
        window.location.href = '/cart';
      } catch (err) {
        console.error('[crea-tu-pack] checkout error', err);
        this._toast(err.message || 'No se pudo añadir al carrito', 'error');
        this._setCheckoutLoading(false);
      }
    });
  }

  _setCheckoutLoading(loading) {
    if (this.cache.checkout) this.cache.checkout.disabled = loading;
    if (this.cache.spinner) this.cache.spinner.hidden = !loading;
  }

  async _submitToCart() {
    const packId = this._uuid();
    const items = this.items.map((item, idx) => {
      const properties = {
        _pack_id: packId,
        _pack_size: String(this.packSize),
        _pack_position: String(idx + 1),
        Pack: `Pack de ${this.packSize} guantes`,
      };
      const p = item.personalization;
      if (p) {
        const right = (p.right || '').trim();
        const left = (p.left || '').trim();
        // Orden requerido en el pedido admin: IZQUIERDO primero, DERECHO
        // segundo. Solo se añaden si tienen texto (así no aparecen líneas
        // vacías en el pedido cuando el cliente no personalizó ese par).
        if (left)  properties['Personalización guante izquierdo'] = left;
        if (right) properties['Personalización guante derecho']  = right;
      }
      return {
        id: parseInt(item.variantId, 10),
        quantity: 1,
        properties,
      };
    });

    // Append the personalization fee once if any glove carries personalization
    if (this.persoVariantId && this._hasAnyPersonalization()) {
      items.push({
        id: parseInt(this.persoVariantId, 10),
        quantity: 1,
        properties: {
          _pack_id: packId,
          Pack: `Pack de ${this.packSize} guantes`,
        },
      });
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.error('[crea-tu-pack] /cart/add.js failed', { status: res.status, body: data, items });
      const msg = data.description || data.message || (data.errors && JSON.stringify(data.errors)) || `Error ${res.status} al añadir al carrito`;
      throw new Error(msg);
    }
    return res.json();
  }

  _clearStateAfterCheckout() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /* ------------------------------------------------------- Modals (info + personalize) */
  _wirePersonalize() {
    // Personalize modal trigger
    if (this.cache.personalizeBtn && this.cache.personalizeModal) {
      this.cache.personalizeBtn.addEventListener('click', () => {
        this._openPersonalizeModal();
      });
      this.cache.personalizeCloseEls.forEach((el) => {
        el.addEventListener('click', () => this._closePersonalizeModal(false));
      });
      if (this.cache.personalizeSaveBtn) {
        this.cache.personalizeSaveBtn.addEventListener('click', () => {
          this._savePersonalization();
        });
      }
    }

    // Info modal trigger
    if (this.cache.infoBtn && this.cache.infoModal) {
      this.cache.infoBtn.addEventListener('click', () => {
        this._openInfoModal();
      });
      this.cache.infoCloseEls.forEach((el) => {
        el.addEventListener('click', () => this._closeInfoModal());
      });
    }

    // Global Escape closes whichever modal is open
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.cache.personalizeModal && !this.cache.personalizeModal.hidden) {
        this._closePersonalizeModal(false);
      } else if (this.cache.infoModal && !this.cache.infoModal.hidden) {
        this._closeInfoModal();
      }
    });
  }

  _openPersonalizeModal() {
    this._renderPersonalizeList();
    this.cache.personalizeModal.hidden = false;
    this._lockBodyScroll();
    this.root.dataset.modalOpen = 'true';
    // Focus first input or close button
    const firstInput = this.cache.personalizeList.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    } else if (this.cache.personalizeCloseEls[0]) {
      this.cache.personalizeCloseEls[0].focus();
    }
  }

  _closePersonalizeModal(_saved) {
    this.cache.personalizeModal.hidden = true;
    this._unlockBodyScroll();
    delete this.root.dataset.modalOpen;
    if (this.cache.personalizeBtn) this.cache.personalizeBtn.focus();
  }

  /* ------------------------------------------------------- Scroll lock */
  // We deliberately DO NOT lock the body's scroll when a modal opens.
  // Setting `overflow: hidden` on <body> reassigns the sticky panel's
  // scroll container from the viewport to the body itself — and since
  // the body then has no scrolling overflow, `position: sticky` collapses
  // to `position: relative`, making the panel jump back to its position
  // in document flow (far below the catalog, out of view).
  // The modal already uses position: fixed so it overlays the page; the
  // user can still scroll the page behind it without breaking sticky.
  _lockBodyScroll() { /* no-op — see comment above */ }
  _unlockBodyScroll() { /* no-op — see comment above */ }

  _renderPersonalizeList() {
    const list = this.cache.personalizeList;
    const empty = this.cache.personalizeEmpty;
    const saveBtn = this.cache.personalizeSaveBtn;
    list.innerHTML = '';

    if (this.items.length === 0) {
      empty.hidden = false;
      if (saveBtn) saveBtn.disabled = true;
      return;
    }
    empty.hidden = true;
    if (saveBtn) saveBtn.disabled = false;

    this.items.forEach((item, idx) => {
      const li = document.createElement('li');
      li.className = 'ctp__personalize-item';
      const personalization = item.personalization || { right: '', left: '' };
      const safeImg = this._escapeAttr(item.imgSrc);
      const safeTitle = this._escapeHtml(item.productTitle);
      const safeSize = this._escapeHtml(item.sizeLabel);
      const safeRight = this._escapeAttr(personalization.right || '');
      const safeLeft = this._escapeAttr(personalization.left || '');
      const idRight = `ctp-pers-${idx}-right`;
      const idLeft = `ctp-pers-${idx}-left`;
      li.innerHTML = `
        <img class="ctp__personalize-thumb" src="${safeImg}" alt="${safeTitle}" loading="lazy">
        <div class="ctp__personalize-info">
          <span class="ctp__personalize-product">
            ${safeTitle}
            <small>Talla ${safeSize}</small>
          </span>
          <div class="ctp__personalize-fields">
            <div class="ctp__personalize-field">
              <label class="ctp__personalize-label" for="${idLeft}">Guante izquierdo</label>
              <input
                id="${idLeft}"
                type="text"
                class="ctp__personalize-input"
                data-side="left"
                data-item-key="${this._escapeAttr(item.key)}"
                maxlength="20"
                placeholder="Ej: NOMBRE"
                value="${safeLeft}"
              >
            </div>
            <div class="ctp__personalize-field">
              <label class="ctp__personalize-label" for="${idRight}">Guante derecho</label>
              <input
                id="${idRight}"
                type="text"
                class="ctp__personalize-input"
                data-side="right"
                data-item-key="${this._escapeAttr(item.key)}"
                maxlength="20"
                placeholder="Ej: APELLIDO"
                value="${safeRight}"
              >
            </div>
          </div>
        </div>
      `;
      list.appendChild(li);
      // Sanitiza cada input en vivo + muestra aviso si se filtra algo.
      const STRIP = /[^\p{L}\p{N} .\-']/gu;
      const WARN_MSG = 'No se permiten emojis ni caracteres especiales.';
      const showWarn = (inp) => {
        let group = inp.closest('.ctp__personalize-field') || inp.parentNode;
        if (!group) return;
        let w = group.querySelector('.ctp__personalize-warn');
        if (!w) {
          w = document.createElement('div');
          w.className = 'ctp__personalize-warn';
          w.setAttribute('role', 'alert');
          w.style.cssText = 'color:#c1121f;font-size:12px;margin-top:4px;line-height:1.35;';
          w.textContent = WARN_MSG;
          group.appendChild(w);
        }
        w.style.display = '';
        clearTimeout(w._t);
        w._t = setTimeout(() => { w.style.display = 'none'; }, 2500);
      };
      li.querySelectorAll('input.ctp__personalize-input').forEach((inp) => {
        inp.addEventListener('input', () => {
          const cleaned = String(inp.value).replace(STRIP, '');
          if (cleaned !== inp.value) {
            const pos = inp.selectionStart;
            inp.value = cleaned;
            try { inp.setSelectionRange(pos, pos); } catch (e) {}
            showWarn(inp);
          }
        });
        inp.addEventListener('paste', (e) => {
          try {
            const d = e.clipboardData || window.clipboardData;
            if (!d) return;
            const t = d.getData('text');
            if (t && t.replace(STRIP, '') !== t) showWarn(inp);
          } catch (er) {}
        });
      });
    });
  }

  _savePersonalization() {
    const inputs = Array.from(this.cache.personalizeList.querySelectorAll('input[data-item-key]'));
    let touched = 0;
    inputs.forEach((input) => {
      const key = input.dataset.itemKey;
      const side = input.dataset.side;
      const item = this.items.find((i) => i.key === key);
      if (!item) return;
      if (!item.personalization) item.personalization = { right: '', left: '' };
      // Strip emojis / símbolos como último cinturón antes de guardar.
      const value = String(input.value || '').replace(/[^\p{L}\p{N} .\-']/gu, '').trim();
      if (item.personalization[side] !== value) touched++;
      item.personalization[side] = value;
    });
    this._saveState();
    this._closePersonalizeModal(true);
    // Refresh totals so the personalization fee (or its removal) shows up
    this._renderTotals();
    this._renderCartBar();
    if (touched > 0) {
      this._toast('Personalización guardada.', 'success');
    }
  }

  _openInfoModal() {
    this.cache.infoModal.hidden = false;
    this._lockBodyScroll();
    this.root.dataset.modalOpen = 'true';
    if (this.cache.infoCloseEls[0]) this.cache.infoCloseEls[0].focus();
  }

  _closeInfoModal() {
    this.cache.infoModal.hidden = true;
    this._unlockBodyScroll();
    delete this.root.dataset.modalOpen;
    if (this.cache.infoBtn) this.cache.infoBtn.focus();
  }

  /* ------------------------------------------------------- Mobile cart bar */
  _wireCartBar() {
    if (!this.cache.cartBar) return;

    if (this.cache.cartToggle) {
      this.cache.cartToggle.addEventListener('click', () => this._toggleCartExpanded());
    }
    this.cache.cartCloseEls.forEach((el) => {
      el.addEventListener('click', () => this._setCartExpanded(false));
    });
    if (this.cache.cartFinalize) {
      this.cache.cartFinalize.addEventListener('click', async () => {
        if (this.cache.cartFinalize.disabled) return;
        try {
          this._setFinalizeLoading(true);
          await this._submitToCart();
          this._clearStateAfterCheckout();
          window.location.href = '/cart';
        } catch (err) {
          console.error('[crea-tu-pack] checkout error', err);
          this._toast(err.message || 'No se pudo añadir al carrito', 'error');
          this._setFinalizeLoading(false);
        }
      });
    }
  }

  _toggleCartBar() {
    if (!this.cache.cartBar) return;
    const isMobile = window.matchMedia('(max-width: 949px)').matches;
    this.cache.cartBar.hidden = !isMobile;
    if (!isMobile) {
      this._setCartExpanded(false);
    }
  }

  _toggleCartExpanded() {
    const isOpen = this.cache.cartBar.dataset.expanded === 'true';
    this._setCartExpanded(!isOpen);
  }

  _setCartExpanded(open) {
    const exp = this.cache.cartExpanded;
    if (!exp) return;
    if (open) {
      this.cache.cartBar.dataset.expanded = 'true';
      exp.hidden = false;
      exp.setAttribute('aria-hidden', 'false');
      if (this.cache.cartToggle) this.cache.cartToggle.setAttribute('aria-expanded', 'true');
      if (this.cache.cartToggleLabel) this.cache.cartToggleLabel.textContent = 'OCULTAR';
      document.body.dataset.ctpMobile = 'open';
    } else {
      this.cache.cartBar.dataset.expanded = 'false';
      exp.hidden = true;
      exp.setAttribute('aria-hidden', 'true');
      if (this.cache.cartToggle) this.cache.cartToggle.setAttribute('aria-expanded', 'false');
      if (this.cache.cartToggleLabel) this.cache.cartToggleLabel.textContent = 'VER CARRITO';
      document.body.dataset.ctpMobile = 'closed';
    }
  }

  _renderCartBar() {
    if (!this.cache.cartBar) return;

    if (this.cache.cartCount) {
      this.cache.cartCount.textContent = `${this.items.length}/${this.packSize}`;
    }

    this._renderCartProgress();

    const subtotal = this.items.reduce((s, i) => s + i.price, 0);
    const isComplete = this.items.length === this.packSize;
    const discountPct = this._discountPctForPack(this.packSize);
    const discountAmount = isComplete ? Math.round(subtotal * discountPct) / 100 : 0;
    const persoFee = this._personalizationFeeCents();
    const total = subtotal - discountAmount + persoFee;

    if (this.cache.cartTotal) {
      this.cache.cartTotal.textContent = this.items.length === 0
        ? '—€'
        : this._formatMoney(Math.round(total));
    }

    // Disable finalize until the pack is fully completed
    if (this.cache.cartFinalize) {
      const canCheckout = this.items.length >= this.packSize;
      this.cache.cartFinalize.disabled = !canCheckout;
      if (this.cache.cartFinalizeLabel) {
        if (canCheckout) {
          this.cache.cartFinalizeLabel.textContent = 'FINALIZAR COMPRA';
        } else {
          const missing = Math.max(0, this.packSize - this.items.length);
          this.cache.cartFinalizeLabel.textContent = `FALTA${missing > 1 ? 'N' : ''} ${missing}`;
        }
      }
    }

    // Render list
    const list = this.cache.cartList;
    const empty = this.cache.cartEmpty;
    if (!list || !empty) return;

    list.innerHTML = '';
    if (this.items.length === 0) {
      empty.hidden = false;
      list.hidden = true;
      return;
    }
    empty.hidden = true;
    list.hidden = false;

    this.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'ctp-cart-item';
      li.innerHTML = `
        <img class="ctp-cart-item__thumb" src="${this._escapeAttr(item.imgSrc)}" alt="${this._escapeAttr(item.productTitle)}" loading="lazy">
        <div class="ctp-cart-item__info">
          <span class="ctp-cart-item__name">${this._escapeHtml(item.productTitle)}</span>
          <span class="ctp-cart-item__size">Talla ${this._escapeHtml(item.sizeLabel)}</span>
        </div>
        <span class="ctp-cart-item__price">${this._formatMoney(item.price)}</span>
        <button type="button" class="ctp-cart-item__remove" aria-label="Quitar ${this._escapeAttr(item.productTitle)} del pack">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      li.querySelector('.ctp-cart-item__remove').addEventListener('click', () => {
        this._removeFromPack(item.key);
      });
      list.appendChild(li);
    });
  }

  _renderCartProgress() {
    const el = this.cache.cartProgress;
    if (!el) return;
    const filled = this.items.length;
    const size = this.packSize;
    if (el.children.length !== size) {
      el.innerHTML = '';
      for (let i = 0; i < size; i += 1) {
        const cell = document.createElement('span');
        cell.className = 'ctp__cart-progress-cell';
        el.appendChild(cell);
      }
    }
    Array.from(el.children).forEach((cell, i) => {
      cell.dataset.state = i < filled ? 'filled' : 'empty';
    });
  }

  _setFinalizeLoading(loading) {
    if (!this.cache.cartFinalize) return;
    if (loading) {
      this.cache.cartFinalize.disabled = true;
      if (this.cache.cartSpinner) this.cache.cartSpinner.hidden = false;
    } else {
      this.cache.cartFinalize.disabled = this.items.length < this.packSize;
      if (this.cache.cartSpinner) this.cache.cartSpinner.hidden = true;
    }
  }

  /* ------------------------------------------------------- State */
  _saveState() {
    try {
      const state = {
        pack_size: this.packSize,
        items: this.items.map((i) => ({
          variantId: i.variantId,
          productId: i.productId,
          productHandle: i.productHandle,
          productTitle: i.productTitle,
          productUrl: i.productUrl,
          sizeLabel: i.sizeLabel,
          price: i.price,
          imgSrc: i.imgSrc,
          key: i.key,
          personalization: i.personalization || null,
        })),
        updated_at: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // ignore (private mode, full quota, etc.)
    }
  }

  _restoreState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state || !state.updated_at) return;
      if (Date.now() - state.updated_at > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      if (this.maxSizes.includes(state.pack_size)) {
        this.packSize = state.pack_size;
        this.root.dataset.packSize = String(state.pack_size);
      }
      if (Array.isArray(state.items)) {
        this.items = state.items.filter((i) => i && i.variantId && i.productId);
      }
    } catch (e) {
      // ignore
    }
  }

  /* ------------------------------------------------------- Helpers */
  _showFullPackToast() {
    const next = this.packSize + 1;
    if (next > 4) {
      this._toast('Tu pack ya está completo.', 'warning');
      return;
    }
    const toast = this.cache.toast;
    toast.hidden = false;
    toast.dataset.variant = 'warning';
    toast.innerHTML = `
      <span>Aumenta tu pack a ${next} guantes para añadir más.</span>
      <button type="button" style="margin-left:12px;background:#fff;color:#000;border:none;padding:4px 10px;border-radius:999px;cursor:pointer;font-weight:700;">Pack ${next}</button>
    `;
    toast.querySelector('button').addEventListener('click', () => {
      this._changePackSize(next);
      toast.hidden = true;
    });
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
  }

  _toast(message, variant = '') {
    const toast = this.cache.toast;
    if (!toast) return;
    toast.textContent = message;
    toast.dataset.variant = variant;
    toast.hidden = false;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.hidden = true; }, 4000);
  }

  _announce(msg) {
    if (!this.cache.announce) return;
    this.cache.announce.textContent = msg;
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `${value}€`;
  }

  _uuid() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
  _escapeAttr(s) {
    return this._escapeHtml(s);
  }
}

function bootstrap() {
  document.querySelectorAll('.ctp[data-section-id]').forEach((root) => {
    if (root.dataset.ctpInitialized === 'true') return;
    root.dataset.ctpInitialized = 'true';
    new CreaTuPack(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// Re-init on theme editor section load
document.addEventListener('shopify:section:load', bootstrap);
